import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError, formatMoney, formatDate, formatDateTime, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import InlineStatus from "@/components/InlineStatus";
import { FABRICACION, EMPAQUE, ENTREGA, FACTURACION } from "@/lib/statuses";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, User as UserIcon, Truck, Receipt as ReceiptIcon, Printer } from "lucide-react";
import { useAuth, hasRole } from "@/context/AuthContext";
import EditarProductosDialog from "@/pages/EditarProductosDialog";
import { ImprimirEtiquetas } from "@/components/domicilios/CargaEntrega";

const PAYMENT_METHODS = ["Efectivo", "Transferencia", "Tarjeta", "Depósito", "QR / Digital"];

export default function PedidoDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [pilotos, setPilotos] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cajas, setCajas] = useState([]);

  // Guarda ubicación de pedido activo en sessionStorage para que Facturación
  // pueda ofrecer "Volver al pedido" al terminar.
  useEffect(() => {
    if (id) {
      try {
        sessionStorage.setItem("last_pedido_id", id);
        // Preservar contexto para el botón Volver: si el pedido fue abierto
        // desde una lista (entregas, hoja de pedidos, archivados), la lista
        // debió guardar `pedidos_return_path`. Si no existe, quedar en null.
        // (No inventar contexto aquí; solo consumirlo en el botón Volver.)
      } catch { /* ignore */ }
    }
  }, [id]);

  // Factura form
  const [monto, setMonto] = useState(0);
  const [qty, setQty] = useState(0);
  const [metodo, setMetodo] = useState("Efectivo");
  const [banco, setBanco] = useState("");
  const [cajaId, setCajaId] = useState("");
  const [ref, setRef] = useState("");
  const [boletaBase64, setBoletaBase64] = useState("");

  // Crear factura form
  const [factNombre, setFactNombre] = useState("");
  const [factNit, setFactNit] = useState("C/F");
  const [factDireccion, setFactDireccion] = useState("");
  const [factNumero, setFactNumero] = useState("");

  // Dialog de anulación (motivo obligatorio)
  const [anulDialog, setAnulDialog] = useState(false);
  const [anulMotivo, setAnulMotivo] = useState("");
  const [anulBusy, setAnulBusy] = useState(false);
  const [anulFacturaNumero, setAnulFacturaNumero] = useState(null); // null=pedido, string=factura
  const [anulFacturaBusy, setAnulFacturaBusy] = useState(false);

  // Dialog de edición de productos (solo admin / gerente_admin)
  const [editItemsOpen, setEditItemsOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
    const saldo = Number(data.saldo_pendiente ?? ((data.total || 0) - (data.monto_facturado_acum || 0)));
    setMonto(saldo > 0 ? saldo.toFixed(2) : 0);
    setQty(data.total_qty || 0);
    // prefill factura data with cliente info
    setFactNombre(data.cliente_nombre || "");
    setFactNit("C/F");
    setFactDireccion(data.direccion || "");
    setFactNumero("");
  };

  useEffect(() => {
    load();
    api.get("/bancos").then(({ data }) => setBancos(data)).catch(() => {});
    api.get("/cajas").then(({ data }) => setCajas((data || []).filter((c) => c.activo !== false))).catch(() => {});
    if (hasRole(user, "admin", "vendedor")) {
      api.get("/users").then(({ data }) =>
        setPilotos(data.filter((u) => u.role === "piloto"))
      ).catch(() => {});
    }
  }, [id]);

  // Auto-scroll to crear-factura section when arriving via hash
  useEffect(() => {
    if (order && (window.location.hash === "#crear-factura" || window.location.hash === "#facturar")) {
      const el = document.getElementById("crear-factura") || document.getElementById("facturar");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [order]);

  if (!order) return <div className="text-zinc-500 text-sm">Cargando...</div>;
  const esAdminFlujo = user?.role === "admin" || user?.role === "gerente_admin";

  const setSub = async (campo, valor) => {
    try {
      const { data } = await api.patch(`/orders/${order.id}/subestado`, { campo, valor });
      setOrder(data);
      toast.success("Estado actualizado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setEstado = async (estado) => {
    try {
      const { data } = await api.patch(`/orders/${order.id}/estado`, { estado });
      setOrder(data);
      toast.success("Estado actualizado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const asignarPiloto = async (piloto_id) => {
    try {
      const { data } = await api.patch(`/orders/${order.id}/asignar-piloto`, { piloto_id });
      setOrder(data);
      toast.success("Piloto asignado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const facturar = async () => {
    try {
      const { data } = await api.patch(`/orders/${order.id}/facturar`, {
        monto_facturado: Number(monto), cantidad_items: Number(qty),
        metodo_pago: metodo, banco, referencia: ref,
      });
      setOrder(data);
      toast.success("Pago registrado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const crearFactura = async () => {
    if (!factNombre.trim()) return toast.error("Ingresa el nombre para la factura");
    try {
      const { data } = await api.patch(`/orders/${order.id}/crear-factura`, {
        nombre_facturar: factNombre.trim(),
        nit: (factNit || "C/F").trim(),
        direccion_facturar: factDireccion.trim(),
        numero_factura: factNumero.trim(),
      });
      setOrder(data);
      toast.success(`Factura ${data.factura_data?.numero_factura} creada`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const agregarFacturaPago = async () => {
    if (!factNombre.trim()) return toast.error("Nombre a facturar es obligatorio");
    const m = Number(monto);
    if (!m || m <= 0) return toast.error("Monto inválido");
    if (!boletaBase64) return toast.error("Foto de la boleta / comprobante es obligatoria");
    try {
      const { data } = await api.post(`/orders/${order.id}/facturas`, {
        monto: m,
        nombre_facturar: factNombre.trim(),
        nit: (factNit || "C/F").trim(),
        direccion_facturar: factDireccion.trim(),
        numero_factura: factNumero.trim(),
        metodo_pago: metodo,
        banco: banco || "",
        caja_id: cajaId || "",
        referencia: ref || "",
        boleta_base64: boletaBase64,
      });
      setOrder(data);
      // Reset form
      const saldo = Number(data.saldo_pendiente || 0);
      setMonto(saldo > 0 ? saldo.toFixed(2) : 0);
      setFactNumero("");
      setRef("");
      setBoletaBase64("");
      setCajaId("");
      const last = data.facturas?.[data.facturas.length - 1];
      toast.success(`Factura ${last?.numero_factura} · Q${last?.monto?.toFixed(2)} registrada`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="pedido-detalle-page">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            try {
              const p = sessionStorage.getItem("pedidos_return_path");
              if (p) {
                sessionStorage.removeItem("pedidos_return_path");
                window.location.href = p;
                return;
              }
            } catch { /* ignore */ }
            window.location.href = "/pedidos";
          }}
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900"
          data-testid="btn-volver-lista"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <Link
          to={`/pedidos/${order.id}/guia`}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#002FA7] border border-zinc-200 rounded-md px-3 py-1.5 hover:bg-zinc-50"
          data-testid="open-guia-btn"
        >
          <Printer className="w-4 h-4" /> Guía de despacho
        </Link>
      </div>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Pedido</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1">{order.numero}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusBadge status={order.estado} />
            <span className="text-xs text-zinc-500">Creado {formatDateTime(order.fecha)}</span>
            {order.anulado && (
              <span className="ml-2 text-[10px] uppercase font-bold px-2 py-1 rounded bg-red-100 text-red-700 border border-red-200">
                ANULADO · {order.motivo_anulacion || "sin motivo"}
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total</div>
          <div className="font-manrope font-black tracking-tight text-4xl">{formatMoney(order.total)}</div>
          {!order.anulado && <ImprimirEtiquetas orderId={order.id} className="mt-3 justify-end" />}
          {/* Anular / Restaurar (sólo gerente_admin y admin) */}
          {user && (user.role === "gerente_admin" || user.role === "admin") && (
            <div className="mt-3 flex gap-2 justify-end">
              {order.anulado ? (
                <Button size="sm" variant="outline" onClick={async () => {
                  if (!window.confirm(`¿Restaurar pedido ${order.numero}?`)) return;
                  try {
                    await api.post(`/orders/${order.id}/restaurar`);
                    toast.success("Pedido restaurado");
                    const { data } = await api.get(`/orders/${order.id}`);
                    setOrder(data);
                  } catch (e) { toast.error(formatApiError(e)); }
                }} data-testid="restaurar-pedido-btn" className="border-emerald-200 text-emerald-700">
                  Restaurar
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => { setAnulFacturaNumero(null); setAnulMotivo(""); setAnulDialog(true); }} data-testid="anular-pedido-btn" className="border-red-200 text-red-700">
                  Anular pedido
                </Button>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column: items */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5 border-zinc-200 rounded-md">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Productos</div>
              {hasRole(user, "admin", "gerente_admin") && !order.anulado && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="editar-productos-btn"
                  onClick={() => setEditItemsOpen(true)}
                  className="text-[11px] h-7"
                >
                  Editar productos
                </Button>
              )}
            </div>
            <div className="mt-3 space-y-2">
              {order.items.map((it, i) => (
                <div key={i} className="flex items-start justify-between border-b border-zinc-100 pb-2 last:border-0">
                  <div className="min-w-0">
                    <div className="font-semibold">{it.product_name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {[it.color, it.tamano, it.estilo].filter(Boolean).join(" · ") || "Sin variante"}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">Cant: {it.cantidad} · {formatMoney(it.precio_unit)}{it.descuento ? ` · -${it.descuento}%` : ""}</div>
                  </div>
                  <div className="text-right font-semibold tabular-nums">{formatMoney(it.subtotal)}</div>
                </div>
              ))}
              <div className="pt-3 grid grid-cols-2 md:grid-cols-5 gap-3 border-t border-zinc-100">
                <div><div className="text-[10px] uppercase text-zinc-500 tracking-widest">Subtotal</div><div className="font-semibold">{formatMoney(order.subtotal)}</div></div>
                <div><div className="text-[10px] uppercase text-zinc-500 tracking-widest">Envío</div><div className="font-semibold">{formatMoney(order.envio_precio)}</div></div>
                <div><div className="text-[10px] uppercase text-zinc-500 tracking-widest">Total</div><div className="font-semibold">{formatMoney(order.total)}</div></div>
                <div>
                  <div className="text-[10px] uppercase text-zinc-500 tracking-widest">Anticipo</div>
                  <div className="font-semibold">{formatMoney(order.anticipo || 0)}</div>
                  {order.anticipo_metodo ? (
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {order.anticipo_metodo}{order.anticipo_autorizacion ? ` · ${order.anticipo_autorizacion}` : ""}
                    </div>
                  ) : null}
                </div>
                <div className="text-right"><div className="text-[10px] uppercase text-emerald-700 tracking-widest">A cobrar</div><div className="font-manrope font-black text-lg text-emerald-700">{formatMoney(order.a_cobrar != null ? order.a_cobrar : (order.total - (order.anticipo || 0)))}</div></div>
              </div>
            </div>
          </Card>

          {/* Historial */}
          <Card className="p-5 border-zinc-200 rounded-md">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Historial</div>
            <ol className="mt-3 space-y-2 text-sm">
              {(order.historial || []).slice().reverse().map((h, i) => (
                <li key={i} className="flex items-start justify-between border-l-2 border-[#002FA7] pl-3">
                  <div>
                    <div className="font-semibold">{h.estado}</div>
                    {h.by && <div className="text-xs text-zinc-500">{h.by}</div>}
                  </div>
                  <div className="text-xs text-zinc-500">{formatDateTime(h.at)}</div>
                </li>
              ))}
            </ol>
          </Card>

          {/* Intentos de entrega */}
          {(order.intentos_entrega || []).length > 0 && (
            <Card className="p-5 border-zinc-200 rounded-md" data-testid="intentos-entrega-card">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Intentos de entrega</div>
              <ol className="mt-3 space-y-2 text-sm">
                {(order.intentos_entrega || []).slice().reverse().map((it, i) => {
                  const RESULT_LABELS = { entregado: "Entregado", parcial: "Parcial", danado: "Dañado", otro_dia: "Otro día", no_contesto: "No contestó", fallido: "Fallido" };
                  const RESULT_CLS = { entregado: "bg-emerald-100 text-emerald-900", parcial: "bg-yellow-100 text-yellow-900", danado: "bg-red-100 text-red-900", otro_dia: "bg-amber-100 text-amber-900", no_contesto: "bg-orange-100 text-orange-900", fallido: "bg-red-100 text-red-900" };
                  return (
                    <li key={i} className="flex items-start justify-between gap-2 border-l-2 border-emerald-500 pl-3">
                      <div className="min-w-0 flex-1">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${RESULT_CLS[it.resultado] || "bg-zinc-200"}`}>
                          {RESULT_LABELS[it.resultado] || it.resultado}
                        </span>
                        {it.nota && <div className="text-xs text-zinc-600 mt-1 break-words">{it.nota}</div>}
                        {it.ruta_fecha && <div className="text-[10px] text-zinc-400">Ruta: {it.ruta_fecha}</div>}
                      </div>
                      <div className="text-[10px] text-zinc-500 whitespace-nowrap">{formatDateTime(it.fecha)}</div>
                    </li>
                  );
                })}
              </ol>
            </Card>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <Card className="p-5 border-zinc-200 rounded-md">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cliente</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex items-center gap-2"><UserIcon className="w-3.5 h-3.5 text-zinc-400" /><span className="font-semibold">{order.cliente_nombre}</span></div>
              {order.cliente_telefono && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-zinc-400" />{order.cliente_telefono}{order.cliente_telefono2 ? <span className="text-zinc-500"> · alt {order.cliente_telefono2}</span> : null}</div>}
              <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-zinc-400 mt-0.5" /><span>{order.direccion}</span></div>
              {order.fecha_entrega && <div className="text-xs text-zinc-500">Entrega prevista: {formatDate(order.fecha_entrega)}</div>}
              <div className="pt-2 border-t border-zinc-100 flex items-center gap-2">
                <Truck className="w-3.5 h-3.5 text-zinc-400" />
                <span className="text-sm">{order.envio_nombre || "Sin envío"} · <span className="capitalize">{order.envio_tipo}</span></span>
              </div>
            </div>
          </Card>

          {/* Sub-estados */}
          <Card className="p-5 border-zinc-200 rounded-md space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Flujo</div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">Fabricación</span>
              <span className="sr-only">Solo consulta: cada estado se cambia en su módulo</span>
              <InlineStatus
                orderId={order.id}
                campo="estado_fabricacion"
                value={order.estado_fabricacion || "pendiente"}
                options={FABRICACION}
                canEdit={esAdminFlujo}
                onChanged={load}
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">Empaque</span>
              <InlineStatus
                orderId={order.id}
                campo="estado_empaque"
                value={order.estado_empaque || "pendiente"}
                options={EMPAQUE}
                canEdit={esAdminFlujo}
                onChanged={load}
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">Entrega ({order.envio_tipo})</span>
              <InlineStatus
                orderId={order.id}
                campo="estado_entrega"
                value={order.estado_entrega || "pendiente"}
                options={ENTREGA}
                canEdit={esAdminFlujo}
                onChanged={load}
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">Facturación</span>
              <InlineStatus
                orderId={order.id}
                campo="estado_facturacion"
                value={order.estado_facturacion || "pendiente_facturar"}
                options={FACTURACION}
                canEdit={esAdminFlujo}
                onChanged={load}
              />
            </div>

            {order.envio_tipo !== "ciudad" && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-600">Departamento / Colocación</span>
                <InlineStatus
                  orderId={order.id}
                  campo="estado_colocacion"
                  value={order.estado_colocacion || "pendiente_empacar"}
                  options={[{ value: "pendiente_empacar", label: "Pendiente de empacar", cls: "bg-zinc-200 text-zinc-800" }, { value: "empacado", label: "Empacado", cls: "bg-amber-100 text-amber-900" }, { value: "trasladando", label: "Trasladando", cls: "bg-orange-100 text-orange-900" }, { value: "colocado", label: "Colocado", cls: "bg-emerald-100 text-emerald-900" }]}
                  canEdit={esAdminFlujo}
                  onChanged={load}
                />
              </div>
            )}
            {!esAdminFlujo && <div className="text-[10px] text-zinc-500" data-testid="flujo-solo-lectura">Solo el Administrador puede modificar estos estados.</div>}

            {hasRole(user, "vendedor") && (
              <div className="pt-2 border-t border-zinc-100">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Asignar piloto</div>
                <Select value={order.piloto_id || ""} onValueChange={asignarPiloto}>
                  <SelectTrigger className="h-9" data-testid="assign-piloto"><SelectValue placeholder={order.piloto_nombre || "Selecciona"} /></SelectTrigger>
                  <SelectContent>
                    {pilotos.length === 0 && <div className="p-3 text-xs text-zinc-500">Crea usuarios con rol “piloto”.</div>}
                    {pilotos.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {order.piloto_nombre && <div className="text-xs text-zinc-500 mt-1">Asignado: {order.piloto_nombre}</div>}
              </div>
            )}

            {hasRole(user, "vendedor") && order.estado !== "cancelado" && order.estado !== "pagado" && (
              <div className="pt-2 border-t border-zinc-100">
                <Button variant="outline" size="sm" className="w-full text-red-600 hover:text-red-700" onClick={() => setEstado("cancelado")}>
                  Cancelar pedido
                </Button>
              </div>
            )}
          </Card>

          {/* Pagos y Facturas (múltiples) */}
          {hasRole(user, "facturacion") && (
            <Card id="crear-factura" className="p-5 border-zinc-200 rounded-md space-y-4 scroll-mt-24">
              <div className="flex items-center gap-2">
                <ReceiptIcon className="w-4 h-4 text-indigo-700" />
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pagos y Facturas</div>
              </div>

              {/* Resumen */}
              {(() => {
                const total = Number(order.total || 0);
                const acum = Number(order.monto_facturado_acum || 0);
                const saldo = Number(order.saldo_pendiente ?? (total - acum));
                const pct = total > 0 ? Math.min(100, (acum / total) * 100) : 0;
                return (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-md bg-zinc-50 border border-zinc-200 p-2">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total pedido</div>
                      <div className="font-semibold tabular-nums" data-testid="fact-total">{formatMoney(total)}</div>
                    </div>
                    <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700">Facturado</div>
                      <div className="font-semibold tabular-nums text-emerald-900" data-testid="fact-acum">{formatMoney(acum)}</div>
                    </div>
                    <div className={`rounded-md border p-2 ${saldo <= 0.005 ? "bg-emerald-50 border-emerald-200" : "bg-orange-50 border-orange-200"}`}>
                      <div className={`text-[10px] uppercase tracking-widest ${saldo <= 0.005 ? "text-emerald-700" : "text-orange-700"}`}>Saldo</div>
                      <div className={`font-semibold tabular-nums ${saldo <= 0.005 ? "text-emerald-900" : "text-orange-900"}`} data-testid="fact-saldo">{formatMoney(saldo)}</div>
                    </div>
                    <div className="col-span-3">
                      <div className="h-2 w-full bg-zinc-100 rounded overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Lista de facturas */}
              {(order.facturas && order.facturas.length > 0) && (
                <div className="space-y-2" data-testid="facturas-list">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Facturas registradas ({order.facturas.length})</div>
                  {order.facturas.map((f, i) => (
                    <div key={i} className="border border-indigo-100 bg-indigo-50/40 rounded-md p-3 text-sm space-y-1" data-testid={`factura-item-${f.numero_factura}`}>
                      <div className="flex items-baseline justify-between">
                        <span className="font-mono font-black text-indigo-900">{f.numero_factura}</span>
                        <span className="font-semibold tabular-nums">{formatMoney(f.monto)}</span>
                      </div>
                      <div className="text-xs text-zinc-600">
                        <span className="font-semibold">{f.nombre_facturar}</span>
                        {f.nit && f.nit !== "C/F" && <span className="text-zinc-500"> · NIT {f.nit}</span>}
                      </div>
                      <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>{f.metodo_pago}</span>
                        {f.banco && <span>· {f.banco}</span>}
                        {f.referencia && <span>· ref {f.referencia}</span>}
                        <span>· {formatDateTime(f.fecha_pago)}</span>
                      </div>
                      {hasRole(user, "admin") && (
                        <button
                          type="button"
                          onClick={() => { setAnulFacturaNumero(f.numero_factura); setAnulMotivo(""); setAnulDialog(true); }}
                          className="text-[10px] text-red-600 hover:text-red-800 uppercase tracking-widest font-semibold cursor-pointer"
                          data-testid={`factura-del-${f.numero_factura}`}
                        >
                          Eliminar
                        </button>
                      )}
                      <a
                        href={`${API_BASE}/orders/${order.id}/facturas/${encodeURIComponent(f.numero_factura)}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-indigo-700 hover:text-indigo-900 uppercase tracking-widest font-semibold ml-2"
                        data-testid={`factura-pdf-${f.numero_factura}`}
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const token = localStorage.getItem("token");
                            const res = await fetch(e.currentTarget.href, {
                              headers: token ? { Authorization: `Bearer ${token}` } : {},
                            });
                            if (!res.ok) throw new Error();
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            window.open(url, "_blank");
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          } catch { toast.error("No se pudo generar PDF"); }
                        }}
                      >
                        PDF
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {/* Form nueva factura (si hay saldo) */}
              {Number(order.saldo_pendiente ?? (order.total || 0) - (order.monto_facturado_acum || 0)) > 0.005 && new Date(order.fecha).toLocaleDateString("en-CA", { timeZone: "America/Guatemala" }) >= "2026-11-04" ? (
                <div className="rounded-md bg-violet-50 border border-violet-200 p-3 text-sm text-violet-900" data-testid="pf-solo-anticipo">
                  En Hoja de Pedidos solo se factura el anticipo. Los demás cobros de este pedido se facturan desde <b>Mi Dinero</b> (reportes de App Domicilios y liquidaciones de departamentos).
                </div>
              ) : Number(order.saldo_pendiente ?? (order.total || 0) - (order.monto_facturado_acum || 0)) > 0.005 ? (
                <div className="space-y-2 pt-2 border-t border-zinc-100">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Nueva factura + pago</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest">Monto (Q)</Label>
                      <Input type="number" min={0} step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} data-testid="pf-monto" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest">N° Factura</Label>
                      <Input value={factNumero} onChange={(e) => setFactNumero(e.target.value)} placeholder="Auto: F-00001" data-testid="pf-numero" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest">Nombre a facturar</Label>
                    <Input value={factNombre} onChange={(e) => setFactNombre(e.target.value)} placeholder="Nombre en la factura" data-testid="pf-nombre" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest">NIT</Label>
                      <Input value={factNit} onChange={(e) => setFactNit(e.target.value)} placeholder="C/F" data-testid="pf-nit" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest">Método pago</Label>
                      <Select value={metodo} onValueChange={setMetodo}>
                        <SelectTrigger className="h-9" data-testid="pf-metodo"><SelectValue /></SelectTrigger>
                        <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest">Dirección (opcional)</Label>
                    <Input value={factDireccion} onChange={(e) => setFactDireccion(e.target.value)} placeholder="Dirección factura" data-testid="pf-direccion" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest">Cuenta (Banco o Caja) donde ingresó el pago</Label>
                    <Select
                      value={cajaId ? `caja:${cajaId}` : (banco ? `banco:${banco}` : "none")}
                      onValueChange={(v) => {
                        if (v === "none") { setBanco(""); setCajaId(""); }
                        else if (v.startsWith("banco:")) { setBanco(v.slice(6)); setCajaId(""); }
                        else if (v.startsWith("caja:")) { setCajaId(v.slice(5)); setBanco(""); }
                      }}
                    >
                      <SelectTrigger className="h-9" data-testid="pf-cuenta"><SelectValue placeholder="Selecciona banco o caja" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {bancos.length > 0 && (
                          <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Bancos</div>
                        )}
                        {bancos.map((b) => <SelectItem key={`b-${b.id}`} value={`banco:${b.nombre}`}>🏦 {b.nombre} ({b.tipo})</SelectItem>)}
                        {cajas.length > 0 && (
                          <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Cajas</div>
                        )}
                        {cajas.map((c) => <SelectItem key={`c-${c.id}`} value={`caja:${c.id}`}>💰 {c.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest">Referencia</Label>
                    <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Nro. comprobante" data-testid="pf-ref" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest">Foto boleta / comprobante <span className="text-red-600">*</span></Label>
                    <input
                      type="file" accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 6 * 1024 * 1024) return toast.error("Máx 6MB");
                        const reader = new FileReader();
                        reader.onload = () => setBoletaBase64(reader.result);
                        reader.readAsDataURL(f);
                      }}
                      className="text-xs mt-1"
                      data-testid="pf-boleta-input"
                    />
                    {boletaBase64 && (
                      <div className="mt-2 flex items-start gap-2">
                        <img src={boletaBase64} alt="boleta" className="max-h-24 max-w-[100px] object-contain rounded border border-zinc-200" />
                        <button type="button" onClick={() => setBoletaBase64("")} className="text-xs text-red-600 underline">Quitar</button>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={agregarFacturaPago}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    data-testid="add-factura-pago-btn"
                  >
                    Registrar pago y crear factura
                  </Button>
                </div>
              ) : (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 font-semibold text-center">
                  ✓ Pedido totalmente facturado y pagado
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      <Dialog open={anulDialog} onOpenChange={(v) => { setAnulDialog(v); if (!v) { setAnulMotivo(""); setAnulFacturaNumero(null); }}}>
        <DialogContent className="max-w-md" data-testid="dialog-motivo-anulacion">
          <DialogHeader>
            <DialogTitle>
              {anulFacturaNumero
                ? `Eliminar factura ${anulFacturaNumero}`
                : `Anular pedido ${order?.numero || ""}`}
            </DialogTitle>
            <DialogDescription>
              Escribe el motivo de {anulFacturaNumero ? "eliminación" : "anulación"} (obligatorio). Se registrará en auditoría junto con la fecha, hora y tu usuario.
              {!anulFacturaNumero && (
                <span className="block mt-2 text-red-600 text-xs font-semibold">
                  Se revertirán en cascada: movimientos de caja, saldos de transportista, gastos de guía y depósitos vinculados.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-600">Motivo <span className="text-red-600">*</span></Label>
            <Textarea
              value={anulMotivo}
              onChange={(e) => setAnulMotivo(e.target.value)}
              rows={4}
              placeholder="Ej. cliente canceló, pedido duplicado, error de captura..."
              autoFocus
              data-testid="input-motivo-anulacion"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnulDialog(false)} disabled={anulBusy || anulFacturaBusy}>Cancelar</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={!anulMotivo.trim() || anulBusy || anulFacturaBusy}
              onClick={async () => {
                if (!anulMotivo.trim()) return;
                if (anulFacturaNumero) {
                  setAnulFacturaBusy(true);
                  try {
                    const { data } = await api.delete(
                      `/orders/${order.id}/facturas/${encodeURIComponent(anulFacturaNumero)}?motivo=${encodeURIComponent(anulMotivo.trim())}`
                    );
                    setOrder(data);
                    toast.success(`Factura ${anulFacturaNumero} eliminada`);
                    setAnulDialog(false);
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setAnulFacturaBusy(false); }
                } else {
                  setAnulBusy(true);
                  try {
                    await api.delete(`/orders/${order.id}?motivo=${encodeURIComponent(anulMotivo.trim())}`);
                    toast.success("Pedido anulado");
                    const { data } = await api.get(`/orders/${order.id}`);
                    setOrder(data);
                    setAnulDialog(false);
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setAnulBusy(false); }
                }
              }}
              data-testid="confirm-motivo-anulacion"
            >
              {(anulBusy || anulFacturaBusy) ? "Procesando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditarProductosDialog
        open={editItemsOpen}
        onOpenChange={setEditItemsOpen}
        order={order}
        onSaved={(updated) => setOrder(updated)}
      />
    </div>
  );
}
